import { useCallback, useEffect, useRef, useState } from 'react';

import type { PdfExportRecordType } from '@gearbelt/global';

import { ApiService } from '@/services/api-v2';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

// Client-side floors shown until the run reports its own progress. Kept below the
// server's first real milestone (the export-pdf task sets 0 → 60 → 85 → 100) so the
// first poll can only advance the bar. Combined with the monotonic clamp below, a
// lower or absent poll value can never step the bar backwards.
const PROGRESS_REQUEST_SENT = 5;
const PROGRESS_REQUEST_ACCEPTED = 10;
const PROGRESS_COMPLETE = 100;

export type SingleRecordPdfExportStatus = 'idle' | 'generating' | 'ready' | 'error';

type GenerateArgs = {
  recordType: PdfExportRecordType;
  recordId: string;
};

/**
 * Drives a single-record PDF export and exposes its live status + progress so
 * the caller can render a blocking loader instead of a blank tab. On completion
 * status becomes `'ready'` and `downloadUrl` holds the PDF URL; the export tab
 * handles opening it (auto + manual). Unmounting or calling `reset` aborts an
 * in-flight poll so it can't set state on a torn-down tree.
 */
export function useSingleRecordPdfExport() {
  const [status, setStatus] = useState<SingleRecordPdfExportStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setStatus('idle');
    setProgress(0);
    setDownloadUrl(null);
  }, []);

  const generate = useCallback(async ({ recordType, recordId }: GenerateArgs) => {
    // Abort any prior run instead of bailing, so a remount that aborted the first run can restart cleanly.
    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;
    setStatus('generating');
    setProgress(PROGRESS_REQUEST_SENT);
    setDownloadUrl(null);

    try {
      const { runId } = await (await ApiService.CreatePdfExport({ recordType, recordId }, abort.signal)).json();
      if (abort.signal.aborted) return;
      setProgress((prev) => Math.max(prev, PROGRESS_REQUEST_ACCEPTED));

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      while (Date.now() < deadline) {
        if (abort.signal.aborted) return;
        const run = await (await ApiService.GetPdfExportRun(runId, abort.signal)).json();
        if (abort.signal.aborted) return;
        if (run.status === 'completed' && run.downloadUrl) {
          setProgress(PROGRESS_COMPLETE);
          setDownloadUrl(run.downloadUrl);
          setStatus('ready');
          return;
        }
        if (run.status === 'failed') {
          throw new Error(`PDF export run ${runId} failed`);
        }
        // Hold the last value when a poll reports no progress and never let the bar
        // decrease; reserve 100% for completion so it never reads done while spinning.
        if (typeof run.progress === 'number') {
          const reported = Math.min(Math.round(run.progress), PROGRESS_COMPLETE - 1);
          setProgress((prev) => Math.max(prev, reported));
        }
        await sleep(POLL_INTERVAL_MS, abort.signal);
      }
      throw new Error(`PDF export run ${runId} timed out`);
    } catch (error) {
      if (abort.signal.aborted) return;
      console.error('Single-record PDF export failed', error);
      setStatus('error');
    }
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  return { status, progress, downloadUrl, generate, reset };
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}
